import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowForward } from '@mui/icons-material';
import { ReportHalftimeCard, ReportResultCard } from '../reports/ReportPerformance';
import { reportGoalIntervals, reportNumber, reportRate, reportRecentMatches, reportSum } from '../../utils/rivalReport';
import { TeamMatchLink } from './TeamStatisticsMatches';

export default function TeamStatisticsOverview({ team, statistics, standing, calendar }) {
  const { t, i18n } = useTranslation('stats');
  const recentTitleId = useId();
  const matches = statistics?.partidos || {};
  const recent = reportRecentMatches(calendar, statistics?.idEquipo, team?.nombre, 5);
  const played = reportNumber(matches.partidosJugados);
  const intervals = reportGoalIntervals(statistics, standing);
  const context = [
    ['overall', '', '', 'partidosJugados'],
    ['home', 'Casa', 'home', 'partidosJugadosLocal'],
    ['away', 'Fuera', 'away', 'partidosJugadosVisitante'],
  ];
  const cleanSheets = reportSum(reportNumber(matches.porteria0FinalLocal) ?? 0, reportNumber(matches.porteria0FinalVisitante) ?? 0);
  const halftimeLead = reportNumber(matches.descansoGanandoTotal) ?? 0;
  const format = value => (value ?? 0).toLocaleString(i18n.language);
  return <>
    <header className="ts-section-heading"><span className="rr-eyebrow">{t('teams.modern.performanceEyebrow')}</span>
      <h2>{t('teams.modern.summaryTitle')}</h2><p>{t('teams.modern.summarySubtitle')}</p></header>
    <div className="ts-results-grid">{context.map(([label, suffix, venue, sample]) =>
      <ReportResultCard key={label} venue={venue} missingValue="0" title={t(`teams.modern.${label}`)} stats={{
        jugados: standing?.[`jugados${suffix}`] ?? matches[sample],
        ganados: standing?.[`ganados${suffix}`], empatados: standing?.[`empatados${suffix}`],
        perdidos: standing?.[`perdidos${suffix}`],
        golesContra: standing?.[`golesContra${suffix}`] ?? (venue
          ? statistics?.datos_extra?.[`sumaGolesRecibidos${venue === 'home' ? 'Local' : 'Visitante'}`]
            ?? reportSum(...intervals.map(row => row[`${venue}Against`])) : undefined),
        golesFavor: standing?.[`golesFavor${suffix}`] ?? (venue
          ? statistics?.datos_extra?.[`sumaGolesMetidos${venue === 'home' ? 'Local' : 'Visitante'}`]
            ?? reportSum(...intervals.map(row => row[`${venue}For`])) : undefined),
      }} />)}</div>
    <section className="rr-panel ts-recent" aria-labelledby={recentTitleId}>
      <header><span className="rr-eyebrow">{t('teams.modern.recentEyebrow')}</span>
        <h2 id={recentTitleId}>{t('teams.modern.recentTitle')}</h2><p>{t('teams.modern.recentSubtitle')}</p></header>
      {recent.length ? <ol className="ts-form">{recent.map((match, index) => <li key={`${match.round}:${index}`}
        className={`ts-form-${match.outcome}`}>
        <TeamMatchLink match={match} round={{ jornada: match.round }} team={team} className="ts-form-link">
        <span>{t('teams.modern.round', { round: match.round ?? '—' })} · {t(`teams.modern.${match.home ? 'home' : 'away'}`)}</span>
        <strong>{match.forGoals} <span>–</span> {match.againstGoals}</strong><b>{match.opponent}</b>
        <small>{t(`teams.modern.outcomes.${match.outcome}`)}</small>
        <ArrowForward className="ts-form-arrow" fontSize="small" />
        </TeamMatchLink>
      </li>)}</ol> : <p className="rr-empty">{t('teams.modern.noRecentMatches')}</p>}
    </section>
    <div className="ts-insights">{[
      ['cleanSheets', cleanSheets], ['halftimeLead', halftimeLead],
    ].map(([label, value]) => {
      const rate = value !== null && played !== null && value <= played ? reportRate(value, played) : null;
      return <section className="rr-panel" key={label}>
        <dl><div><dt className="rr-eyebrow">{t(`teams.modern.${label}`)}</dt><dd>{format(value)}</dd></div></dl>
        <p>{rate === null ? t('teams.modern.recordedData') : t('teams.modern.matchShare', {
          share: rate.toLocaleString(i18n.language, { style: 'percent', maximumFractionDigits: 1 }),
        })}</p>
      </section>;
    })}</div>
    <ReportHalftimeCard matches={matches} missingValue="0" />
  </>;
}
